import "@conectate/components/ct-button.js";
import "@conectate/components/ct-icon.js";
import "@conectate/components/ct-spinner";

import { AuthenticationResult, Configuration, InteractionRequiredAuthError, LogLevel, PopupRequest, PublicClientApplication, RedirectRequest } from "@azure/msal-browser";
import { showCtConfirm } from "@conectate/components/ct-confirm.js";
import { closeCtDialog } from "@conectate/components/ct-dialog.js";
import { sleep } from "@conectate/components/ct-helpers";
import { CtLit, css, customElement, html, property, state } from "@conectate/components/ct-lit";
import { getAuth, signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";

import { icons } from "../../@components/ct-icons/ct-icons.js";
import { MatchKeysAndValues } from "../../@components/interfaces.js";
import { strings } from "../../@strings/strings.js";
import apiv2 from "../../api/v2/API";
import { CTlog } from "../../xconfig/app-logs.js";
import { sayHi } from "../../xconfig/utils.js";

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
const tokenRequest = {
	scopes: ["User.Read", "Mail.Read"],
	forceRefresh: false // Set this to "true" to skip a cached token and go to the server to get a new token
};

/**
 * Configure you `Microsoft Entra ID`
 * Azure Portal > Microsoft Entra ID > Manage > App registrations > Your App > Application (client) ID
 * https://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredApps
 * https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade
 */
@customElement("ct-login-microsoft")
export class CtLoginMicrosoft extends CtLit {
	static styles = css`
		:host {
			display: block;
			margin: 16px auto;
			position: relative;
			max-width: 400px;
		}

		ct-icon {
			min-width: 24px;
		}
		ct-button.microsoft {
			display: block;
			border-radius: var(--border-radius, 16px);
			padding: 16px;
			text-align: center;
			background: #2f2f2f;
			color: #fff;
			box-shadow: 0 4px 28px 4px #2f2f2f22;
		}
	`;
	@property({ type: String }) client_id = "26739acf-4791-4381-819e-71fc5ad1ed36";
	@property({ type: String }) signin_txt = strings.signin_google.replace(/Google/i, "Microsoft/Outlook");
	@property({ type: Boolean }) use_redirect = false;
	@property({ type: Object }) handleSigninFlow?: (response: AuthenticationResult) => any;
	/**
	 * No recargar la página al iniciar sesión
	 */
	@property({ type: Boolean, reflect: true }) public prevent_redirect: boolean = false;
	/**
	 * Esto es para restringir si deseamos que solo cierto dominios de Google Workspace puedan iniciar sesión.
	 * Por ejemplo su pongo grupoconectate.com, cualquier otro dominio ej gmail.com no lo dejará continuar
	 */
	@property({ type: Array }) public restrictDomains: string[] = [];
	@property({ type: Boolean }) public onlyenterprise = false;

	@state() private loading = false;
	@state() private hi = "";

	private graphConfig = {
		graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
		graphMailEndpoint: "https://graph.microsoft.com/v1.0/me/messages"
	};

	private get msalConfig(): Configuration {
		return {
			auth: {
				// 'Application (client) ID' of app registration in Azure portal - this value is a GUID
				clientId: this.client_id,
				// Full directory URL, in the form of https://login.microsoftonline.com/<tenant-id>
				// authority: "Enter_the_Cloud_Instance_Id_HereEnter_the_Tenant_Info_Here",
				// Full redirect URL, in form of http://localhost:3000
				redirectUri: location.origin + "/login"
			},
			cache: {
				cacheLocation: "sessionStorage", // This configures where your cache will be stored
				storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
			},
			system: {
				loggerOptions: {
					loggerCallback: (level, message, containsPii) => {
						if (containsPii) {
							return;
						}
						switch (level) {
							case LogLevel.Error:
								closeCtDialog();
								console.error(message);
								return;
							case LogLevel.Info:
								console.info(message);
								return;
							case LogLevel.Verbose:
								console.debug(message);
								return;
							case LogLevel.Warning:
								console.warn(message);
								return;
						}
					}
				}
			}
		};
	}
	private myMSALObj = new PublicClientApplication(this.msalConfig);

	private loginRequest: RedirectRequest | PopupRequest = {
		scopes: ["User.Read"],
		prompt: "select_account" // login, none, consent, select_account, create, no_session
		// extraQueryParameters: { domain_hint: "*" } // Restrict to enterprise accounts
	};
	private response?: AuthenticationResult;

	private signedInEmail?: string;

	constructor(js?: MatchKeysAndValues<CtLoginMicrosoft>) {
		super();
		if (js) {
			Object.assign(this, js);
		}
	}
	render() {
		return html`
			<ct-button id="btn" raised class="microsoft" @click=${this.signIn} ?disabled=${this.loading}>
				<div style="display: flex;align-items: center;white-space: normal;">
					${this.loading
						? html`<ct-spinner style="margin-right: 12px;"></ct-spinner> <span style="color: #fff;">${this.hi}</span>`
						: html`<ct-icon .svg=${icons.ic_microsoft} style="margin-right: 12px;"></ct-icon> ${this.signin_txt}`}
				</div>
			</ct-button>
		`;
	}
	protected firstUpdated() {
		this.selectAccount();
		if (this.use_redirect)
			this.myMSALObj
				.handleRedirectPromise()
				.then(r => this.handleResponse(r!))
				.catch(error => {
					console.error(error);
				});
	}

	private selectAccount() {
		/**
		 * See here for more info on account retrieval:
		 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
		 */

		const currentAccounts = this.myMSALObj.getAllAccounts();
		console.log(`currentAccounts`, currentAccounts);
		if (currentAccounts.length === 0) {
			return;
		} else if (currentAccounts.length > 1) {
			// Add choose account code here
			console.warn("Multiple accounts detected.");
		} else if (currentAccounts.length === 1) {
			this.signedInEmail = currentAccounts[0].username;
			console.log(currentAccounts[0].username);
		}
	}

	private async signIn() {
		/**
		 * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
		 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
		 */
		sessionStorage.clear();
		await this.myMSALObj.initialize();
		if (this.use_redirect) {
			this.myMSALObj.loginRedirect(this.loginRequest);
		} else {
			this.loading = true;
			let [rsp, err] = await this.myMSALObj.loginPopup(this.loginRequest).go();
			if (err) {
				this.loading = false;
				return console.error(err);
			}
			this.response = rsp;
			this.handleResponse(this.response);
		}
	}

	private async handleResponse(response?: AuthenticationResult) {
		if (!response) return;
		/**
		 * To see the full list of response object properties, visit:
		 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
		 */
		if (this.handleSigninFlow) {
			return this.handleSigninFlow(response);
		}

		if (this.response !== null) {
			this.signedInEmail = this.response?.account?.username!;
			debugger;
			if (this.restrictDomains.length > 0 || this.onlyenterprise) {
				let domain = this.signedInEmail.split("@")[1];
				if (this.onlyenterprise && ["gmail", "msn", "hotmail", "outlook", "live", "onmicrosoft.com"].some(d => domain.includes(d))) {
					showCtConfirm(strings.error_invalid_email + " " + domain, strings.must_user_business_email);
					return;
				}
				if (this.restrictDomains.length > 0 && !this.restrictDomains.includes(domain)) {
					showCtConfirm(
						"Dominio no permitido",
						`El dominio de tu correo <b>${domain}</b> no esta permitido en esta red. Usa un correo con dominio <b>${this.restrictDomains.join(", ")}</b>`
					);
					return;
				}
			}

			this.hi = `${sayHi()} ${this.response?.account?.name} - ${strings.loading}`;

			let [providers, err] = await apiv2.users.verifyIfExists({ email: this.response?.account?.username! });
			if (providers.exists) {
				// El el correo ya existe porque uso otro proveedor aqui se manejara,
				// Y si todo esta bien entonces iniciara normal
				let { json, error } = await apiv2.users.getCustomTokenWithMicrosoft({ access_token: this.response?.accessToken! });
				await signInWithCustomToken(getAuth(), json.token);
				CTlog.login.login(getAuth().currentUser?.uid!, "microsoft");
				if (this.prevent_redirect) {
					this.loading = false;
					return;
				}
				await sleep(400);
				this.redirect();
			} else {
				let { json } = await apiv2.users.createUserWithMicrosoft(this.response?.accessToken!);
				if (json.created) {
					CTlog.login.register("microsoft");
					// Haciendo el login
					let user = await signInWithEmailAndPassword(getAuth(), json.email, json.pass);

					try {
						var cred = new PasswordCredential({
							id: getAuth().currentUser?.email!,
							name: getAuth().currentUser?.displayName,
							password: json.pass,
							iconURL: getAuth().currentUser?.photoURL || window.app.constants.placeholderPNG
						});
						navigator.credentials.store(cred);
					} catch (e) {
						console.error(`Navigator Credentials dosen't support`, e);
					}
					// TODO: Lo linkedo con la credecial de google
					/*  var credential = firebase.auth.GoogleAuthProvider.credential(r.credential);
						await getAuth().currentUser?.linkWithCredential(credential); */

					// ✅✅✅✅✅✅✅✅USUARIO CREADO ✅✅✅✅✅✅✅✅
					await sleep(400);
					this.redirect({ showCreated: true });
				} else {
					this.loading = false;
					this.hi = "";
					showCtConfirm("Ha ocurrido un error", "Parece algo fallo al crear tu cuenta con Google, intenta con el registro normal");
				}
			}
		} else {
			this.selectAccount();
		}
	}

	private async redirect(params?: { showCreated?: boolean }) {
		// this.getContacts();
		debugger;
		this.dispatchEvent(new CustomEvent("success"));
		if (this.prevent_redirect) {
			return;
		}
		let isEmpleoORATS = window.app.hostname.includes("empleo") || window.ats_config;
		let url = isEmpleoORATS ? "/assistant" : "/";
		if (window.app.redirectURL) {
			url = window.app.redirectURL;
		}
		if (location.pathname == "/login") {
			window.href(url);
			await sleep(400);
			if (params?.showCreated) showCtConfirm(strings.register_welcome, strings.your_account_has_created);
		} else {
			window.app.reload();
		}
	}

	async signOut() {
		/**
		 * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
		 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
		 */
		console.log(this.msalConfig.auth.clientId);
		const currentAccounts = this.myMSALObj.getAllAccounts();
		console.log(`currentAccounts`, currentAccounts);
		debugger;
		if (currentAccounts.length == 0) {
			location.href = "/";
			return window.app.reload();
		}
		await this.myMSALObj.initialize();
		for (const account of currentAccounts) {
			const logoutRequest = {
				account: this.myMSALObj.getAccountByUsername(account.username),
				postLogoutRedirectUri: this.msalConfig.auth.redirectUri,
				mainWindowRedirectUri: this.msalConfig.auth.redirectUri
			};
			await this.myMSALObj.logoutRedirect(logoutRequest);
		}
	}

	private seeProfile() {
		interface Profile {
			"@odata.context": string;
			businessPhones: any[];
			displayName: string;
			givenName: string;
			jobTitle: string;
			mail: string;
			mobilePhone: null;
			officeLocation: null;
			preferredLanguage: string;
			surname: string;
			userPrincipalName: string;
			id: string;
		}

		this.getTokenPopup(this.loginRequest)
			?.then(response => {
				if (response) {
					this.response = response;
					this.signedInEmail = this.response?.account?.username!;
					this.callMSGraph(this.graphConfig.graphMeEndpoint, response.accessToken, (s: Profile) => {
						console.log("Callback");
						console.log(s);
					});
				}
			})
			.catch(error => {
				console.error(error);
			});
	}

	private getTokenPopup(request: PopupRequest) {
		/**
		 * See here for more info on account retrieval:
		 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
		 */
		let account = this.myMSALObj.getAccountByUsername(this.signedInEmail!);

		if (!account) {
			return;
		}
		request.account = account;
		return this.myMSALObj.acquireTokenSilent(request).catch(error => {
			console.warn("silent token acquisition fails. acquiring token using popup");
			if (error instanceof InteractionRequiredAuthError) {
				// fallback to interaction when silent call fails
				return this.myMSALObj
					.acquireTokenPopup(request)
					.then(tokenResponse => {
						console.log(tokenResponse);
						return tokenResponse;
					})
					.catch(error => {
						console.error(error);
					});
			} else {
				console.warn(error);
			}
		});
	}

	/**
	 * Helper function to call MS Graph API endpoint
	 * using the authorization bearer token scheme
	 */
	async callMSGraph(endpoint: string, token: string, callback: (r: any, end: string) => any) {
		const headers = new Headers();
		const bearer = `Bearer ${token}`;
		headers.append("Authorization", bearer);
		console.log("request made to Graph API at: " + new Date().toString());
		return fetch(endpoint, {
			method: "GET",
			headers: headers
		})
			.then(response => response.json())
			.then(response => callback(response, endpoint))
			.catch(error => console.log(error));
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ct-login-microsoft": CtLoginMicrosoft;
	}
}
